import * as React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, DescriptionItem, DescriptionList } from "@/components/ui/card";
import { CopyButton } from "@/components/ui/misc";
import { contractCard, contractStatus, contractSteps, laterStep, type ContractStep } from "@/lib/contract-history";
import { useLeadContracts } from "@/lib/queries";
import type { LeadContract } from "@/lib/types";
import { cn, formatDateTime } from "@/lib/utils";

/**
 * The record's Contract card (ACL-427): what became of each press of Send contract, so a rep sees
 * whether the agreement went out, was opened or was signed without opening the contract app.
 *
 * The newest press that made an agreement is shown with its steps and their times, so a press
 * refused after it (a duplicate) never hides an agreement the lead has or has signed; that press is
 * said on one line beneath it, and the rest fold under it (`contractCard`). It reads
 * `GET /leads/{id}/contracts` (ACL-422, ADR-158 in platform), kept under the contact's key, so a
 * send and the `leads` hint a signature raises (ACL-425) read it again. A lead with no press, and a
 * core-api without the route, draw nothing. The signing link is offered to copy wherever core-api
 * returned it (it blanks it for a viewer the form hides it from), and never opened from here: whoever
 * opens it is taken for the lead.
 */
export function ContractHistoryCard({ leadId }: { leadId: string }) {
  const contracts = useLeadContracts(leadId);
  const [earlierOpen, setEarlierOpen] = React.useState(false);
  const shown = contractCard(contracts.data ?? []);
  if (!shown) return null;
  const { lead, later, others, newer } = shown;
  const status = contractStatus(lead);
  const kind = newer ? "other" : "earlier";
  return (
    <Card>
      <CardHeader title="Contract" action={<Badge tone={status.tone}>{status.label}</Badge>} />
      <ContractPress row={lead} later={later ? laterStep(later) : null} />
      {others.length > 0 && (
        <div className="border-t border-border px-4 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2.5"
            aria-expanded={earlierOpen}
            onClick={() => setEarlierOpen((open) => !open)}
          >
            {earlierOpen
              ? `Hide ${kind} sends`
              : `Show ${others.length} ${kind} ${others.length === 1 ? "send" : "sends"}`}
          </Button>
          {earlierOpen && (
            <ul className="mt-1 divide-y divide-border">
              {others.map((row) => (
                <li key={row.id}>
                  <EarlierPress row={row} />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

function ContractPress({ row, later = null }: { row: LeadContract; later?: ContractStep | null }) {
  return (
    <DescriptionList className="px-4">
      {contractSteps(row).map((step) => (
        <DescriptionItem key={step.label} label={step.label}>
          <StepValue step={step} />
        </DescriptionItem>
      ))}
      {row.signing_url && (
        <DescriptionItem label="Signing link">
          <span className="flex min-w-0 items-center gap-1">
            <span className="truncate font-mono text-xs text-text-muted">{row.signing_url}</span>
            <CopyButton value={row.signing_url} label="Copy signing link" />
          </span>
        </DescriptionItem>
      )}
      {later && (
        <DescriptionItem label={later.label}>
          <StepValue step={later} />
        </DescriptionItem>
      )}
    </DescriptionList>
  );
}

function StepValue({ step }: { step: ContractStep }) {
  // Beside a time the detail is a smaller second line; alone, it is the value itself.
  const tone = step.state === "bad" ? "text-danger-11" : step.state === "open" || step.at ? "text-text-muted" : null;
  return (
    <>
      {step.at && <span className="block">{formatDateTime(step.at)}</span>}
      {step.detail && <span className={cn("block", step.at && "text-xs", tone)}>{step.detail}</span>}
    </>
  );
}

/** An older press: its status and agreement on one line, and its steps beneath. */
function EarlierPress({ row }: { row: LeadContract }) {
  const status = contractStatus(row);
  return (
    <div className="py-2">
      <div className="flex items-center gap-2 text-sm">
        <Badge tone={status.tone} size="sm">{status.label}</Badge>
        <span className="min-w-0 truncate">{row.agreement ?? "No agreement chosen"}</span>
      </div>
      <div className="-mx-4">
        <ContractPress row={row} />
      </div>
    </div>
  );
}
